// ============================================================================
// Stage 1 Audit Export — log lengkap semua signals + decision path per kandidat
// ============================================================================
// Per KES spec — operator harus bisa export hasil Stage 1 sebagai bukti audit:
// (1) kandidat mana di-discover dari source mana, (2) signals biometric +
// nama + handle + lokasi + cross-platform + bonuses, (3) keputusan tier
// (HIGH/MEDIUM/LOW/DISCARDED) + alasan, (4) timeline (kapan masuk pipeline,
// kapan face match done), (5) ALIAS floor diterapkan atau tidak.
//
// Output JSON (machine-readable) + CSV summary (operator-readable).
// ============================================================================

/**
 * Build audit JSON dari faceMatchStream + composite scores + run metadata.
 * @param {object} args - { subject, faceMatchStream, platformStates, hints,
 *                          imageSerpStats, risStatus, runId, startedAt,
 *                          finishedAt, biometricGate }
 * @returns {object} JSON dengan struktur run + per-candidate audit trail
 */
export function buildAuditTrail(args) {
  const {
    subject = null,
    faceMatchStream = [],
    platformStates = {},
    hints = {},
    imageSerpStats = null,
    risStatus = null,
    runId = null,
    startedAt = null,
    finishedAt = null,
    biometricGate = null,
  } = args || {};

  const now = new Date().toISOString();

  // Run-level metadata
  const runMeta = {
    run_id: runId,
    started_at: startedAt,
    finished_at: finishedAt || now,
    exported_at: now,
    subject: subject ? {
      nik: subject.nik,
      nama_lengkap: subject.nama_lengkap,
      kota_kabupaten: subject.kota_kabupaten || subject.kota_kab_ktp,
      foto_url: subject.foto_url,
    } : null,
    hints,  // username, lokasiTerkini, tempatKerja, keywords[]
    biometric_gate: biometricGate || null,
  };

  // Per-radar discovery summary
  const radarSummary = {};
  for (const [platform, state] of Object.entries(platformStates)) {
    radarSummary[platform] = {
      phase: state.phase,
      count: state.count || 0,
      face_matches_above_threshold: state.face_matches_above_threshold,
      total_images: state.total_images,
      reason: state.reason,
    };
  }

  // Per-candidate audit trail
  const candidates = faceMatchStream.map((entry, idx) => {
    const comp = entry._composite || null;
    return {
      index: idx + 1,
      candidate_id: entry.id,
      platform: entry.platform,
      handle: entry.handle,
      display_name: entry.display_name,
      photo_url: entry.photo_url,
      avatar_url: entry.avatar_url,
      source_url: entry.source_url,
      source: entry.source,  // discovery origin (radar_a_ddg / radar_b_yandex / radar_c_apify / radar_d_ddg / radar_d_yandex / yandex_ris / universal_unavatar_multi)
      engine: entry.engine,
      status: entry.status,
      // Raw biometric signals
      face: {
        similarity: entry.similarity,
        similarity_max: entry.similarity_max,
        similarity_mean_top3: entry.similarity_mean_top3,
        subject_poses_used: entry.subject_poses_used,
        tier: entry.tier,
        photo_match: entry.photo_match,
        photos_scanned: entry.photos_scanned,
        photos_with_face: entry.photos_with_face,
        photos_matching: entry.photos_matching,
        all_scores: entry.all_scores || [],  // top 5 photo scores per kandidat
        scanned_photos: (entry.scanned_photos || []).map(p => ({
          url: p.url, similarity: p.similarity, no_face: !!p.no_face,
        })),
      },
      // Identity signals
      identity: {
        name_match: entry.name_match,
        username_match: entry.username_match,
        disguised: !!entry.disguised,
      },
      // Composite score breakdown (KES Methodology v1.0)
      composite: comp ? {
        final_score: comp.score,
        score_cap: comp.score_cap,
        tier: comp.tier,
        auto_confirm: !!comp.auto_confirm,
        alias_detected: !!comp.alias_detected,
        alias_floor_applied: !!comp.alias_floor_applied,
        biometric_floor_applied: comp.biometric_floor_applied || null,  // addendum v2 2026-05-19
        data_elevated: !!comp.data_elevated,
        elevated_types: comp.elevated_types || [],
        breakdown: comp.breakdown,   // { biometric, name, handle, location, cross, base_total }
        bonuses: comp.bonuses,        // { nik, keywords, employer, location }
        raw_signals: comp.raw_signals,
        biometric_gate_warning: comp.biometric_gate_warning || null,
      } : null,
      // Decision path (human-readable)
      decision_path: buildDecisionPath(entry, comp),
    };
  });

  return {
    schema_version: '1.0',
    schema_doc: 'docs/METHODOLOGY_STAGE1_CONFIDENCE_SCORE.md',
    run: runMeta,
    discovery: {
      radars: radarSummary,
      yandex_ris: risStatus,
      image_serp: imageSerpStats,
    },
    candidates_total: candidates.length,
    candidates,
    summary: buildSummary(candidates),
  };
}

/**
 * Decision path — sequence of rules yang mengarah ke tier kandidat.
 * Operator bisa baca step-by-step kenapa kandidat ini masuk MEDIUM/LOW/dst.
 */
function buildDecisionPath(entry, comp) {
  const path = [];

  path.push(`Discovered via ${entry.source || 'unknown'} (${entry.platform || 'no_platform'})`);

  if (entry.photos_scanned > 1) {
    path.push(`Deep-scanned ${entry.photos_scanned} photos (${entry.photos_with_face || 0} with face)`);
  } else {
    path.push(`Single-photo scan (avatar)`);
  }

  if (entry.subject_poses_used > 1) {
    path.push(`Voted against ${entry.subject_poses_used} subject poses (weighted 0.7×max + 0.3×mean_top3)`);
  }

  if (entry.similarity != null) {
    path.push(`Face similarity: ${(entry.similarity * 100).toFixed(1)}% → tier "${entry.tier}"`);
  }

  if (!comp) {
    path.push('Composite score NOT computed (entry pre-composite or missing data)');
    return path;
  }

  path.push(`Composite base: biometric ${comp.breakdown?.biometric ?? '?'} + name ${comp.breakdown?.name ?? '?'} + handle ${comp.breakdown?.handle ?? '?'} + location ${comp.breakdown?.location ?? '?'} + cross ${comp.breakdown?.cross ?? '?'} = ${comp.breakdown?.base_total ?? '?'}`);

  const bonusTotal = (comp.bonuses?.nik || 0) + (comp.bonuses?.keywords || 0) + (comp.bonuses?.employer || 0) + (comp.bonuses?.location || 0);
  if (bonusTotal > 0) {
    path.push(`Bonuses additive: NIK +${comp.bonuses.nik || 0} keywords +${comp.bonuses.keywords || 0} employer +${comp.bonuses.employer || 0} lokasi +${comp.bonuses.location || 0} = +${bonusTotal}`);
  }

  if (comp.alias_detected) {
    path.push(`ALIAS detected: biometric ≥0.65 + pure levenshtein <0.30 (wajah cocok kuat, nama beda)`);
  }
  if (comp.biometric_floor_applied) {
    const bf = comp.biometric_floor_applied;
    path.push(`BIOMETRIC FLOOR (${bf.label.toUpperCase()}) applied: wajah ≥${Math.round(bf.threshold * 100)}% → score di-floor ke ${bf.floor} (addendum v2 2026-05-19)`);
  } else if (comp.alias_floor_applied) {
    path.push(`ALIAS FLOOR applied: score di-floor ke 50 (MEDIUM minimum)`);
  }

  if (comp.score_cap < 100) {
    path.push(`Score cap: ${comp.score_cap} (${comp.biometric_gate_warning || 'biometric gate active'})`);
  }

  path.push(`Final score: ${comp.score} → tier "${comp.tier}"`);

  if (comp.auto_confirm) {
    path.push(`AUTO badge triggered (score ≥85 AND name_pts ≥17.5)`);
  }

  return path;
}

/**
 * Summary aggregate per tier + key stats. Untuk overview operator sebelum
 * deep-dive ke per-kandidat audit.
 */
function buildSummary(candidates) {
  const tiers = { HIGH: 0, MEDIUM: 0, LOW: 0, DISCARDED: 0, UNCATEGORIZED: 0 };
  const sources = {};
  let aliasCount = 0;
  let aliasFloorCount = 0;
  let autoCount = 0;
  let elevatedCount = 0;

  for (const c of candidates) {
    const t = c.composite?.tier;
    if (t && tiers[t] != null) tiers[t]++;
    else tiers.UNCATEGORIZED++;

    if (c.composite?.alias_detected) aliasCount++;
    if (c.composite?.alias_floor_applied) aliasFloorCount++;
    if (c.composite?.auto_confirm) autoCount++;
    if (c.composite?.data_elevated) elevatedCount++;

    const src = c.source || 'unknown';
    sources[src] = (sources[src] || 0) + 1;
  }

  return {
    tiers,
    sources,
    alias_detected_count: aliasCount,
    alias_floor_applied_count: aliasFloorCount,
    auto_confirm_count: autoCount,
    data_elevated_count: elevatedCount,
  };
}

/**
 * Convert audit JSON → CSV summary (operator-readable). Cuma high-level cols.
 */
export function auditToCSV(audit) {
  if (!audit?.candidates) return '';
  const rows = [];
  rows.push([
    'idx', 'platform', 'handle', 'display_name', 'source',
    'face_similarity', 'face_tier',
    'composite_score', 'composite_tier',
    'name_match', 'photos_scanned', 'photos_matching',
    'alias', 'alias_floor', 'auto_confirm',
    'photo_url', 'source_url',
  ].join(','));
  for (const c of audit.candidates) {
    const row = [
      c.index,
      csvEsc(c.platform),
      csvEsc(c.handle),
      csvEsc(c.display_name),
      csvEsc(c.source),
      c.face?.similarity ?? '',
      csvEsc(c.face?.tier),
      c.composite?.final_score ?? '',
      csvEsc(c.composite?.tier),
      c.identity?.name_match ?? '',
      c.face?.photos_scanned ?? '',
      c.face?.photos_matching ?? '',
      c.composite?.alias_detected ? '1' : '0',
      c.composite?.alias_floor_applied ? '1' : '0',
      c.composite?.auto_confirm ? '1' : '0',
      csvEsc(c.photo_url),
      csvEsc(c.source_url),
    ];
    rows.push(row.join(','));
  }
  return rows.join('\n');
}

function csvEsc(val) {
  if (val == null) return '';
  const s = String(val);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/**
 * Trigger browser download of audit JSON + CSV.
 * Filename: stage1-audit-{NIK}-{timestamp}.{ext}
 */
export function downloadAudit(audit, format = 'json') {
  const subject = audit?.run?.subject;
  const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  const nikSlug = subject?.nik || 'unknown-nik';

  let blob, filename;
  if (format === 'csv') {
    blob = new Blob([auditToCSV(audit)], { type: 'text/csv;charset=utf-8' });
    filename = `stage1-audit-${nikSlug}-${ts}.csv`;
  } else {
    blob = new Blob([JSON.stringify(audit, null, 2)], { type: 'application/json' });
    filename = `stage1-audit-${nikSlug}-${ts}.json`;
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
